import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Award, Star, Clock, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine, SectionHead } from "@/components/Reveal";
import {
  CLINIC,
  IMAGES,
  TREATMENTS,
  CATEGORIES,
  TOP_TREATMENTS,
  PACKAGES,
  TESTIMONIALS,
  GALLERY,
  STATS,
  FEATURED_CONDITIONS,
  DOCTOR,
} from "@/lib/site";

const HERO_BG =
  "https://images.pexels.com/photos/7789601/pexels-photo-7789601.jpeg?auto=compress&cs=tinysrgb&w=1920";
const CTA_BG =
  "https://images.unsplash.com/photo-1706353399656-210cca727a33?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <div data-testid="home-page">
      <Seo
        title="Kamana Physiotherapy & Rehab Center | Best Physiotherapy in Gomti Nagar, Lucknow"
        description="Reclaim your life with Lucknow's trusted physiotherapy and rehab center in Gomti Nagar. Dr. Vipul Singh, University Gold Medalist — pain relief, sports injury, neuro & post-surgery rehab."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative bg-teal-obsidian text-alabaster grain-dark overflow-hidden">
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
          <img
            src={HERO_BG}
            alt=""
            className="absolute inset-x-0 -top-28 h-[calc(100%+14rem)] w-full object-cover object-center"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-teal-obsidian via-teal-obsidian/70 to-teal-obsidian/25" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-teal-obsidian via-teal-obsidian/20 to-teal-obsidian/55" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-32 lg:pt-40 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 border border-gold/40 rounded-full px-4 py-2"
            >
              <Award size={14} className="text-gold" />
              <span className="mono-label text-gold">Led by University Gold Medalist Dr. Vipul Singh</span>
            </motion.div>

            <h1 className="font-display font-semibold tracking-tight leading-[1.04] text-5xl sm:text-6xl lg:text-7xl mt-6">
              <MaskedLine delay={0.15}>Restoring Movement.</MaskedLine>
              <MaskedLine delay={0.32}>
                <span className="italic text-gold">Reclaiming</span> Your Life.
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7 }}
              className="mt-6 text-alabaster/70 text-base sm:text-lg leading-relaxed max-w-xl"
            >
              Lucknow's trusted physiotherapy &amp; rehabilitation centre in Gomti Nagar — where
              pain is treated at its root, recovery is measured, and every session is one-on-one.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/book-appointment"
                data-testid="hero-book-cta"
                className="group inline-flex items-center gap-2 bg-gold text-teal-obsidian font-semibold rounded-full px-7 py-4 text-sm hover:bg-alabaster transition-colors duration-300"
              >
                Book Your Assessment
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={CLINIC.phoneHref}
                data-testid="hero-call-cta"
                className="inline-flex items-center gap-2 border border-alabaster/30 text-alabaster font-semibold rounded-full px-7 py-4 text-sm hover:bg-alabaster/10 transition-colors duration-300"
              >
                <Phone size={16} /> {CLINIC.phone}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.7 }}
              className="mt-9 flex flex-wrap gap-3"
            >
              {[
                { icon: Clock, text: "Open Today · 9 AM – 9 PM" },
                { icon: MapPin, text: "Gomti Nagar, Lucknow" },
                { icon: Award, text: "Gold Medalist-led Care" },
              ].map((chip) => (
                <span
                  key={chip.text}
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 px-4 py-2.5 text-xs font-semibold text-alabaster"
                >
                  <chip.icon size={13} className="text-gold" /> {chip.text}
                </span>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative border-t border-alabaster/10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="text-center lg:text-left" data-testid={`stat-${i}`}>
                  <p className="font-display text-3xl lg:text-4xl font-semibold text-gold">{s.value}</p>
                  <p className="mono-label text-alabaster/50 mt-1.5 !text-[9px]">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <Marquee dark />

      {/* BENTO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <SectionHead label="Why Kamana" title="The Clinic Where Recovery\nIs Designed, Not Rushed" />
          <Reveal delay={0.15}>
            <p className="text-inkmuted max-w-sm text-sm leading-relaxed lg:text-right">
              Four promises that shape every session at our Gomti Nagar rehab centre.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-5">
          <Reveal className="col-span-12 lg:col-span-7" delay={0}>
            <div className="h-full bg-teal-brand text-alabaster rounded-3xl p-8 lg:p-10 relative overflow-hidden card-lift">
              <p className="mono-label text-gold">Clinical Protocol</p>
              <h3 className="font-display text-2xl lg:text-4xl font-semibold mt-4 leading-tight">
                Evidence-Based Neuromuscular Restoration
              </h3>
              <p className="mt-4 text-alabaster/70 text-sm lg:text-base leading-relaxed max-w-md">
                Customised biomechanical protocols crafted personally by Dr. Vipul Singh — every
                plan is built on assessment findings, not guesswork, and audited week by week.
              </p>
              <img
                src={IMAGES.postureAssess.src}
                alt={IMAGES.postureAssess.alt}
                className="absolute -bottom-8 -right-8 w-56 h-56 object-cover rounded-2xl rotate-3 hidden sm:block opacity-90"
              />
            </div>
          </Reveal>

          <Reveal className="col-span-12 lg:col-span-5" delay={0.1}>
            <div className="h-full bg-gold-soft border border-gold/20 rounded-3xl p-8 lg:p-10 card-lift">
              <p className="mono-label text-gold-deep">Modalities</p>
              <h3 className="font-display text-2xl lg:text-3xl font-semibold mt-4 text-teal-brand leading-tight">
                Precision Advanced Equipment
              </h3>
              <p className="mt-4 text-inkmuted text-sm leading-relaxed">
                Class-IV laser, ultrasound therapy, cervical–lumbar traction, IFT and hands-on
                manual mobilisation — combined in the right dose for your condition.
              </p>
            </div>
          </Reveal>

          <Reveal className="col-span-12 lg:col-span-4" delay={0.05}>
            <div className="h-full bg-teal-soft border border-teal-brand/10 rounded-3xl p-8 card-lift">
              <p className="mono-label text-teal-brand">Availability</p>
              <h3 className="font-display text-2xl font-semibold mt-4 text-teal-brand leading-tight">
                Open 7 Days a Week
              </h3>
              <p className="mt-4 text-inkmuted text-sm leading-relaxed">
                Mon–Sat 9 AM–9 PM, Sunday mornings 9–1. Emergency pain-relief walk-ins welcome.
              </p>
            </div>
          </Reveal>

          <Reveal className="col-span-12 lg:col-span-8" delay={0.15}>
            <div className="h-full bg-white border border-teal-brand/10 rounded-3xl p-8 lg:p-10 card-lift flex flex-col sm:flex-row gap-8 items-center">
              <img
                src={IMAGES.assistedMobility.src}
                alt={IMAGES.assistedMobility.alt}
                className="w-full sm:w-56 h-48 sm:h-44 object-cover rounded-2xl"
              />
              <div>
                <p className="mono-label text-gold-deep">Patient First</p>
                <h3 className="font-display text-2xl lg:text-3xl font-semibold mt-3 text-teal-brand leading-tight">
                  One-on-One Dedicated Recovery Pods
                </h3>
                <p className="mt-3 text-inkmuted text-sm leading-relaxed">
                  Zero rush. Every patient receives full 45–60 minute undivided manual therapy and
                  guided motor retraining — never a shared therapist.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TOP TREATMENTS */}
      <section className="bg-teal-soft/60 border-y border-teal-brand/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <SectionHead label="Signature Care" title="Treatments Patients\nTravel Across Lucknow For" />
            <Reveal delay={0.15}>
              <Link
                to="/treatments"
                data-testid="home-all-treatments-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-teal-brand"
              >
                View all 23 treatments
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOP_TREATMENTS.map((name, i) => {
              const t = TREATMENTS.find((x) => x.name === name);
              if (!t) return null;
              const cat = CATEGORIES.find((c) => c.id === t.cat);
              return (
                <Reveal key={t.name} delay={i * 0.07}>
                  <div className="h-full bg-white border border-teal-brand/10 rounded-3xl p-7 card-lift flex flex-col">
                    <span className="mono-label text-gold-deep !text-[9px]">{cat?.label}</span>
                    <h3 className="font-display text-2xl font-semibold text-teal-brand mt-3">{t.name}</h3>
                    <p className="text-inkmuted text-sm leading-relaxed mt-3 flex-1">{t.desc}</p>
                    <Link
                      to="/book-appointment"
                      data-testid={`home-treatment-book-${i}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-teal-brand hover:text-gold-deep transition-colors"
                    >
                      Book for this <ArrowRight size={14} />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* DR. VIPUL SPOTLIGHT */}
      <section className="bg-teal-obsidian text-alabaster grain-dark relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="absolute -inset-3 border border-gold/25 rounded-[2rem] -translate-x-3 -translate-y-3" aria-hidden="true" />
                <img
                  src={IMAGES.drPhoto.src}
                  alt={`${DOCTOR.name}, Chief Consultant Physiotherapist at Kamana Physiotherapy`}
                  className="relative rounded-[1.75rem] w-full h-[420px] lg:h-[540px] object-cover object-top img-spotlight"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <SectionHead dark label="Meet Your Specialist" title={DOCTOR.name} />
            <Reveal delay={0.1}>
              <p className="mono-label text-gold mt-2">{DOCTOR.title} · {DOCTOR.quals}</p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-3 text-alabaster/60 text-sm">{DOCTOR.creds.join("  ·  ")}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-alabaster/70 leading-relaxed max-w-xl">{DOCTOR.intro}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 grid grid-cols-2 gap-4 max-w-xl">
                {DOCTOR.specializations.slice(0, 4).map((s) => (
                  <div key={s} className="flex items-center gap-2.5 text-sm text-alabaster/80">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" />
                    {s}
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <Link
                to="/about"
                data-testid="home-about-doctor-link"
                className="group mt-9 inline-flex items-center gap-2 border border-gold/50 text-gold font-semibold rounded-full px-7 py-4 text-sm hover:bg-gold hover:text-teal-obsidian transition-colors duration-300"
              >
                Full Profile &amp; Awards
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONDITIONS STRIP */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
        <SectionHead label="Conditions Treated" title="If Pain Has a Name,\nWe've Treated It" />
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3">
            {FEATURED_CONDITIONS.map((c) => (
              <Link
                key={c.name}
                to="/conditions"
                data-testid={`home-condition-chip-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="text-sm font-medium border border-teal-brand/15 bg-white rounded-full px-5 py-2.5 text-inkmuted hover:border-teal-brand hover:text-teal-brand transition-colors duration-200"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* PRICING PREVIEW */}
      <section className="bg-gold-soft/70 border-y border-gold/15">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <SectionHead label="Packages & Pricing" title="Honest Pricing,\nComplete Recovery" />
            <Reveal delay={0.15}>
              <Link
                to="/pricing"
                data-testid="home-pricing-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-teal-brand"
              >
                Full pricing details
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <div
                  className={`h-full rounded-3xl p-7 flex flex-col card-lift ${
                    p.popular
                      ? "bg-teal-brand text-alabaster shadow-xl shadow-teal-brand/20"
                      : "bg-white border border-teal-brand/10"
                  }`}
                >
                  <span className={`mono-label !text-[9px] ${p.popular ? "text-gold" : "text-gold-deep"}`}>
                    {p.tag}
                  </span>
                  <h3 className={`font-display text-xl font-semibold mt-3 ${p.popular ? "text-alabaster" : "text-teal-brand"}`}>
                    {p.name}
                  </h3>
                  <p className={`mt-3 font-display text-3xl font-semibold ${p.popular ? "text-gold" : "text-teal-brand"}`}>
                    {p.price}
                  </p>
                  <p className={`text-xs mt-1 ${p.popular ? "text-alabaster/60" : "text-inkmuted"}`}>{p.duration}</p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {p.features.slice(0, 3).map((f) => (
                      <li key={f} className={`text-xs leading-relaxed ${p.popular ? "text-alabaster/75" : "text-inkmuted"}`}>
                        + {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/book-appointment"
                    data-testid={`home-package-book-${i}`}
                    className={`mt-6 text-center text-sm font-semibold rounded-full py-3 transition-colors ${
                      p.popular
                        ? "bg-gold text-teal-obsidian hover:bg-alabaster"
                        : "border border-teal-brand/25 text-teal-brand hover:bg-teal-soft"
                    }`}
                  >
                    Book This
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28">
        <SectionHead label="Patient Stories" title="Recoveries We're\nProud to Have Guided" />
        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <figure className="h-full bg-white border border-teal-brand/10 rounded-3xl p-8 card-lift flex flex-col">
                <div className="flex gap-1 text-gold">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star key={s} size={14} fill="currentColor" />
                  ))}
                </div>
                <blockquote className="mt-4 font-display text-lg lg:text-xl italic leading-relaxed text-teal-brand flex-1">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-teal-brand/10">
                  <p className="text-sm font-semibold text-teal-brand">{t.name}</p>
                  <p className="text-xs text-inkmuted mt-1">
                    {t.condition} · {t.locality} · {t.timeframe}
                  </p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="bg-teal-soft/60 border-y border-teal-brand/10 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <SectionHead label="Inside the Clinic" title="A Space Built\nfor Recovery" />
            <Reveal delay={0.15}>
              <Link to="/gallery" data-testid="home-gallery-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-teal-brand">
                Tour the full gallery
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-5 px-5 lg:mx-0 lg:px-0 snap-x">
            {GALLERY.slice(0, 6).map((g, i) => (
              <Reveal key={i} delay={i * 0.05} className="shrink-0 snap-start">
                <img
                  src={g.src}
                  alt={g.alt}
                  className={`rounded-2xl object-cover ${
                    i % 3 === 0 ? "h-64 w-80" : "h-64 w-64"
                  }`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-teal-obsidian text-alabaster grain-dark relative overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={CTA_BG} alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-teal-obsidian/85" />
          <div className="absolute inset-0 hero-glow" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 py-24 lg:py-32 text-center">
          <Reveal>
            <p className="mono-label text-gold">Your First Step Is Small</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 leading-[1.06]">
              The best time to fix your pain<br className="hidden sm:block" /> was{" "}
              <span className="italic text-gold">last year.</span> The second best is today.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/book-appointment"
                data-testid="home-bottom-book-cta"
                className="bg-gold text-teal-obsidian font-semibold rounded-full px-8 py-4 text-sm hover:bg-alabaster transition-colors duration-300"
              >
                Book Your Assessment
              </Link>
              <a
                href={CLINIC.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid="home-bottom-whatsapp-cta"
                className="border border-alabaster/30 font-semibold rounded-full px-8 py-4 text-sm hover:bg-alabaster/10 transition-colors duration-300"
              >
                WhatsApp Us Now
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
